import * as React from 'react';
import * as SiteDialog from '@/components/ui/dialog';
import * as OsDialog from '@/OS/components/ui/dialog';

type EmbeddedProp = {
  embedded?: boolean;
};

const EmbeddedDialogContext = React.createContext<boolean | undefined>(undefined);

const detectEmbedded = () => typeof document !== 'undefined' && Boolean(document.querySelector('.mapleos-root'));

const useEmbeddedDialog = (embedded?: boolean) => embedded ?? React.useContext(EmbeddedDialogContext) ?? detectEmbedded();

export function Dialog({ embedded, children, ...props }: React.ComponentProps<typeof SiteDialog.Dialog> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.Dialog : SiteDialog.Dialog) as typeof SiteDialog.Dialog;

  return (
    <EmbeddedDialogContext.Provider value={currentEmbedded}>
      <Component {...props}>{children}</Component>
    </EmbeddedDialogContext.Provider>
  );
}

export const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogTrigger>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogTrigger> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogTrigger : SiteDialog.DialogTrigger) as typeof SiteDialog.DialogTrigger;

  return <Component ref={ref as never} {...props} />;
});
DialogTrigger.displayName = 'OSAwareDialogTrigger';

export function DialogPortal({ embedded, ...props }: React.ComponentProps<typeof SiteDialog.DialogPortal> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogPortal : SiteDialog.DialogPortal) as typeof SiteDialog.DialogPortal;

  return <Component {...props} />;
}

export const DialogClose = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogClose>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogClose> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogClose : SiteDialog.DialogClose) as typeof SiteDialog.DialogClose;

  return <Component ref={ref as never} {...props} />;
});
DialogClose.displayName = 'OSAwareDialogClose';

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogOverlay>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogOverlay> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogOverlay : SiteDialog.DialogOverlay) as typeof SiteDialog.DialogOverlay;

  return <Component ref={ref as never} {...props} />;
});
DialogOverlay.displayName = 'OSAwareDialogOverlay';

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogContent>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogContent> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogContent : SiteDialog.DialogContent) as typeof SiteDialog.DialogContent;

  return <Component ref={ref as never} {...props} />;
});
DialogContent.displayName = 'OSAwareDialogContent';

export function DialogHeader({ embedded, ...props }: React.ComponentProps<typeof SiteDialog.DialogHeader> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogHeader : SiteDialog.DialogHeader) as typeof SiteDialog.DialogHeader;

  return <Component {...props} />;
}

export function DialogFooter({ embedded, ...props }: React.ComponentProps<typeof SiteDialog.DialogFooter> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogFooter : SiteDialog.DialogFooter) as typeof SiteDialog.DialogFooter;

  return <Component {...props} />;
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogTitle>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogTitle> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogTitle : SiteDialog.DialogTitle) as typeof SiteDialog.DialogTitle;

  return <Component ref={ref as never} {...props} />;
});
DialogTitle.displayName = 'OSAwareDialogTitle';

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof SiteDialog.DialogDescription>,
  React.ComponentPropsWithoutRef<typeof SiteDialog.DialogDescription> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedDialog(embedded);
  const Component = (currentEmbedded ? OsDialog.DialogDescription : SiteDialog.DialogDescription) as typeof SiteDialog.DialogDescription;

  return <Component ref={ref as never} {...props} />;
});
DialogDescription.displayName = 'OSAwareDialogDescription';
